import type {
  EnrollmentReportItem,
  FinancialSummaryReportItem,
  MonitoringReportItem,
  PaginatedReportResponse,
  ReportEndpointType,
  ReportExportParams,
  ReportsQueryParams,
  SaeSummaryReportItem,
  SitePerformanceReportItem,
} from "@/types/reports.type";
import type { IReportsRepository } from "@/repositories/ReportsRepository";

export interface IReportsUseCase {
  getEnrollmentReport(
    params: ReportsQueryParams,
    allPages?: boolean,
  ): Promise<PaginatedReportResponse<EnrollmentReportItem>>;
  getSitePerformanceReport(
    params: ReportsQueryParams,
    allPages?: boolean,
  ): Promise<PaginatedReportResponse<SitePerformanceReportItem>>;
  getMonitoringReport(
    params: ReportsQueryParams,
  ): Promise<PaginatedReportResponse<MonitoringReportItem>>;
  getSaeSummaryReport(
    params: ReportsQueryParams,
  ): Promise<PaginatedReportResponse<SaeSummaryReportItem>>;
  getFinancialSummaryReport(
    params: ReportsQueryParams,
  ): Promise<PaginatedReportResponse<FinancialSummaryReportItem>>;
  exportReport(
    reportType: ReportEndpointType,
    params: ReportExportParams,
  ): Promise<Blob>;
}

export class ReportsUseCase implements IReportsUseCase {
  constructor(private readonly reportsRepository: IReportsRepository) {}

  async getEnrollmentReport(
    params: ReportsQueryParams,
    allPages = false,
  ): Promise<PaginatedReportResponse<EnrollmentReportItem>> {
    if (allPages) {
      return this.getCompleteReport(params, (query) =>
        this.reportsRepository.getEnrollmentReport(query),
      );
    }
    return this.reportsRepository.getEnrollmentReport(params);
  }

  async getSitePerformanceReport(
    params: ReportsQueryParams,
    allPages = false,
  ): Promise<PaginatedReportResponse<SitePerformanceReportItem>> {
    if (allPages) {
      return this.getCompleteReport(params, (query) =>
        this.reportsRepository.getSitePerformanceReport(query),
      );
    }
    return this.reportsRepository.getSitePerformanceReport(params);
  }

  async getMonitoringReport(
    params: ReportsQueryParams,
  ): Promise<PaginatedReportResponse<MonitoringReportItem>> {
    return this.reportsRepository.getMonitoringReport(params);
  }

  async getSaeSummaryReport(
    params: ReportsQueryParams,
  ): Promise<PaginatedReportResponse<SaeSummaryReportItem>> {
    return this.reportsRepository.getSaeSummaryReport(params);
  }

  async getFinancialSummaryReport(
    params: ReportsQueryParams,
  ): Promise<PaginatedReportResponse<FinancialSummaryReportItem>> {
    return this.reportsRepository.getFinancialSummaryReport(params);
  }

  async exportReport(
    reportType: ReportEndpointType,
    params: ReportExportParams,
  ): Promise<Blob> {
    return this.reportsRepository.exportReport(reportType, params);
  }

  private async getCompleteReport<T>(
    params: ReportsQueryParams,
    fetchPage: (
      query: ReportsQueryParams,
    ) => Promise<PaginatedReportResponse<T>>,
  ): Promise<PaginatedReportResponse<T>> {
    const first = await fetchPage({ ...params, page: 1 });
    const items = [...first.items];
    let page = 1;
    while (items.length < first.total) {
      const next = await fetchPage({ ...params, page: ++page });
      if (!next.items.length || next.total !== first.total) {
        throw new Error(
          "Report changed while loading. Refresh before exporting.",
        );
      }
      items.push(...next.items);
    }
    return { ...first, items };
  }
}
